import { joinSurveyAction } from '../join-survey';
import type { ConnectChannel } from '../types';

/**
 * Static content for the App channel detail page (`/connect/app`). Same
 * sourcing rule as demographics.ts: copied, not imported, since the source
 * (Data Catalog / EXPLORE_CHANNELS) backs internal-only tooling and this
 * page is visible to every Home user.
 */
export const APP_CHANNEL: ConnectChannel = {
    name: 'App',
    icon: 'smartphone',
    color: '#418363',
    subtitle: 'iOS + Android · all installed apps',
    subtitleLine2: 'Daily · Since 2016',
    exploreDaniPrompt: 'What can app usage data tell me about my consumers?',
    actions: [
        {
            id: 'app-benchmark',
            name: 'Benchmark apps',
            icon: 'smartphone',
            summary: 'how much time people spend in your app each day next to competing apps',
            template:
                'Compare daily active minutes for {brand|my} app vs. {competitors|my top 3 competitor apps} over {window|the last 12 weeks}',
        },
        {
            id: 'app-attention',
            name: 'Measure attention',
            icon: 'pie-chart',
            summary: 'how much attention your app gets compared with competing apps',
            template:
                'How much attention does {brand|my} app get compared to {competitors|competitor apps} over {window|the last 12 weeks}?',
        },
        {
            id: 'app-sessions',
            name: 'Track sessions',
            icon: 'activity',
            summary: "how long and how often people use your app, and how that's changing",
            template:
                'Track session length and frequency trends for {brand|my} app over {window|the last 12 weeks}',
        },
        joinSurveyAction('app'),
    ],
    schema: [
        { field: 'consumer_id', type: 'string', description: 'Pseudonymous panel ID' },
        { field: 'app_name', type: 'string', description: 'App display name' },
        {
            field: 'app_category',
            type: 'string',
            description: 'e.g. Communication, Social, Shopping',
        },
        { field: 'session_date', type: 'date', description: 'Session date' },
        { field: 'active_minutes', type: 'float', description: 'Foreground time' },
        { field: 'total_minutes', type: 'float', description: 'Total session length' },
        { field: 'time_of_day', type: 'string', description: 'Daypart bucket' },
    ],
    sampleCols: ['App', 'Category', 'Date', 'Active', 'Total', 'Daypart'],
    sampleColWidths: ['minmax(120px, 1.5fr)', 'minmax(110px, 1fr)', '90px', '70px', '70px', '80px'],
    sampleRows: [
        ['Phone', 'Communication', '2026-04-02', '2.12 min', '2.12 min', 'Evening'],
        ['Gmail', 'Communication', '2026-04-02', '0.22 min', '0.22 min', 'Mid-Day'],
        ['Messenger', 'Communication', '2026-04-02', '1.37 min', '5.40 min', 'Morning'],
        ['SMS/MMS', 'Communication', '2026-04-02', '4.13 min', '4.13 min', 'Morning'],
        ['Samsung In-Call UI', 'Communication', '2026-04-02', '0.62 min', '0.62 min', 'Morning'],
    ],
};
